
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { motion } from 'framer-motion'
import { 
  Bars3Icon, 
  BellIcon, 
  UserCircleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  CogIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'

const Header = ({ sidebarOpen, setSidebarOpen }) => {
  const { user, logout } = useAuth()

  return (
    <header className="bg-slate-900/80 backdrop-blur-xl border-b border-white/10 shadow-2xl">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            className="lg:hidden p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Bars3Icon className="h-6 w-6" />
          </motion.button>

          {/* Search bar */}
          <div className="hidden sm:block flex-1 max-w-lg">
            <div className="relative">
              <input
                type="text"
                placeholder="Search trades, symbols, strategies..."
                className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 focus:bg-white/20 transition-all duration-200"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* AI Status Indicator */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border border-cyan-500/30 rounded-full backdrop-blur-sm"
          >
            <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-white">AI Active</span>
            <SparklesIcon className="w-3 h-3 text-cyan-400" />
          </motion.div>

          {/* Notifications */}
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative p-2.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
          >
            <BellIcon className="h-6 w-6" />
            <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full border-2 border-slate-900"></span>
          </motion.button>

          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center space-x-3 p-2 text-sm rounded-xl hover:bg-white/10 transition-all duration-200">
              <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white font-semibold text-sm">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-white font-medium">{user?.firstName} {user?.lastName}</div>
                <div className="text-xs text-gray-400 capitalize">{user?.subscriptionTier} Plan</div>
              </div>
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 bg-slate-800/95 backdrop-blur-xl rounded-2xl shadow-2xl ring-1 ring-white/10 border border-white/20 focus:outline-none z-50">
                <div className="p-2">
                  {/* User info */}
                  <div className="px-3 py-3 border-b border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{user?.firstName} {user?.lastName}</div>
                        <div className="text-xs text-gray-400">{user?.email}</div>
                      </div>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="py-2">
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="/profile"
                          className={`${
                            active ? 'bg-white/10' : ''
                          } flex items-center px-3 py-2 text-sm text-gray-300 hover:text-white rounded-xl transition-colors`}
                        >
                          <UserCircleIcon className="w-4 h-4 mr-3 text-gray-400" />
                          Your Profile
                        </a>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="/settings"
                          className={`${
                            active ? 'bg-white/10' : ''
                          } flex items-center px-3 py-2 text-sm text-gray-300 hover:text-white rounded-xl transition-colors`}
                        >
                          <CogIcon className="w-4 h-4 mr-3 text-gray-400" />
                          Settings
                        </a>
                      )}
                    </Menu.Item>
                  </div>

                  {/* Logout */}
                  <div className="pt-2 border-t border-white/10">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={`${
                            active ? 'bg-red-500/20' : ''
                          } flex items-center w-full px-3 py-2 text-sm text-red-400 hover:text-red-300 rounded-xl transition-colors`}
                        >
                          <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  )
}

export default Header